<template>
  <div>
    <GmapMap
      style="height:100vh;width:100%;"
      :center="{
        lat: parseFloat(center.lat),
        lng: parseFloat(center.lng)
      }"
      :zoom="14"
      ref="mapRef"
    >
      <GmapInfoWindow
        :options="infoWindowOptions"
        :position="infoWindowPosition"
        :opened="infoWindowOpen"
        @closeclick="infoWindowOpen = false"
      />
      <GmapCustomMarker
        v-for="(p, index) in patients"
        :key="index"
        :marker="{
          lat: parseFloat(p.last_location.lat),
          lng: parseFloat(p.last_location.long)
        }"
        :clickable="true"
        @click.native="toggleInfoWindow(p, index)"
      >
        <div class="custom-marker">
          <div class="custom-marker__marker" />
          <div
            class="custom-marker__image"
            :style="`background-image: url(${p.photo || photoFallback});`"
          />
        </div>
      </GmapCustomMarker>
    </GmapMap>
  </div>
</template>

<script>
import Vue from "vue";
import * as VueGoogleMaps from "vue2-google-maps";
import { operatorMethods } from "./../state/helpers";
import { parseISO, format } from "date-fns";
import { pt as locale } from "date-fns/locale";
import GmapCustomMarker from "vue2-gmap-custom-marker";

Vue.use(VueGoogleMaps, {
  load: { key: process.env.VUE_APP_GOOGLE_MAPS_API_KEY }
});

export default {
  name: "Map",
  components: {
    GmapCustomMarker
  },
  data() {
    return {
      map: null,
      center: {
        lat: 0,
        lng: 0
      },
      patients: [],
      infoWindowPosition: null,
      infoWindowOpen: false,
      currentPatientIndex: null,
      infoWindowOptions: {
        content: "",
        pixelOffset: {
          width: 0,
          height: -40
        }
      },
      interval: null
    };
  },
  async mounted() {
    await this.$getLocation({})
      .then(coordinates => {
        this.center.lat = coordinates.lat;
        this.center.lng = coordinates.lng;
      })
      .catch(err => {
        return err;
      });

    this.$refs.mapRef.$mapPromise.then(map => (this.map = map));

    this.loadUsersLocation();

    this.updateMap();
  },
  beforeDestroy() {
    clearInterval(this.interval);
  },
  methods: {
    ...operatorMethods,
    loadUsersLocation() {
      this.fetchUsersLocation().then(response => {
        this.patients = response.data.filter(user => {
          return user.last_location;
        });
      });
    },
    toggleInfoWindow(patient, index) {
      this.infoWindowPosition = {
        lat: parseFloat(patient.last_location.lat),
        lng: parseFloat(patient.last_location.long)
      };
      this.infoWindowOptions.content = this.infoWindowTemplate(patient);

      if (this.currentPatientIndex == index) {
        this.infoWindowOpen = !this.infoWindowOpen;
      } else {
        this.infoWindowOpen = true;
        this.currentPatientIndex = index;
      }
    },
    infoWindowTemplate(patient) {
      return `
      <div class="info-window">
        <div class="info-window__top">
           <div class="info-window__avatar">
             <div class="info-window__avatar-image" style="background: url(${patient.photo ||
               this.photoFallback})"></div>
           </div>

           <div class="info-window__patient">
             <h4 class="info-window__patient-name">${patient.name}</h4>
             <small class="info-window__patient-description">Paciente</small>
           </div>
        </div>

        <div class="info-window__infor">
          <ul class="info-window__infor-list">
            <div class="info-window__infor-group">
              <li class="info-window__infor-item">
                <h6 class="info-window__infor-subtitle">Tipo sanguíneo:</h6>
                <h6 class="info-window__infor-title">${patient.blood_type ||
                  "Não definido"}</h6>
              </li>

              <li class="info-window__infor-item">
                <h6 class="info-window__infor-subtitle">Idade:</h6>
                <h6 class="info-window__infor-title">${this.formattedAge(
                  patient.birthday
                ) || "Não definido"}</h6>
              </li>
            </div>
            
            <div class="info-window__infor-group">
              <li class="info-window__infor-item">
                <h6 class="info-window__infor-subtitle">Altura:</h6>
                <h6 class="info-window__infor-title">${this.formattedHeight(
                  patient.height
                ) || "Não definido"}</h6>
              </li>

              <li class="info-window__infor-item">
                <h6 class="info-window__infor-subtitle">Peso:</h6>
                <h6 class="info-window__infor-title">${this.formattedWeight(
                  patient.weight
                ) || "Não definido"}</h6>
              </li>
            </div>

            <div class="info-window__infor-group">
              <li class="info-window__infor-item">
                <h6 class="info-window__infor-subtitle">Grupo de risco:</h6>
                <h6 class="info-window__infor-title">${this.patientRisk(
                  patient
                )}</h6>
              </li>

              <li class="info-window__infor-item">
                <h6 class="info-window__infor-subtitle">Última atualização:</h6>
                <h6 class="info-window__infor-title">${this.formattedLocationDate(
                  patient.last_location.created_at
                )}</h6>
              </li>
            </div>

            <div class="info-window__infor-group">
              <li class="info-window__infor-item">
                <button id="button4" class="button4">Mais detalhes</button>
              </li>
            </div>
          </ul>
        </div>
      </div>
      `;
    },
    formattedLocationDate(lastLocationDate) {
      const date = parseISO(lastLocationDate);
      const formattedDate = format(date, "dd 'de' MMMM', ' HH:mm", { locale });
      return formattedDate;
    },
    formattedHeight(height) {
      return height ? `${height}cm` : height;
    },
    formattedWeight(weight) {
      return weight ? `${weight}Kg` : weight;
    },
    formattedAge(birthday) {
      if (birthday) {
        const date = new Date();
        const currentYear = date.getFullYear();
        const splittedBirthday = birthday.split("-");
        return `${currentYear - parseFloat(splittedBirthday)} Anos`;
      }
      return birthday;
    },
    patientRisk(patient) {
      return patient.higher_risk ? "Sim" : "Não";
    },
    updateMap() {
      this.interval = setInterval(() => {
        this.loadUsersLocation();
      }, process.env.VUE_APP_GOOGLE_MAPS_UPDATE_INTERVAL);
    }
  },
  computed: {
    photoFallback() {
      return require("./../assets/images/doctor-img.jpg");
    }
  }
};
</script>
